import React, { useEffect, useState } from 'react';
import * as signalR from '@microsoft/signalr';
import { useParams, useSearchParams } from 'react-router-dom';
import LoadingSpinner from '../components/LoadingSpinner';
import Introduction from './Introduction';
import Viewer from './Viewer';
import './Session.scss';
import { getBrowserDetails } from '../helpers/browsers';
import Background from '../components/Background';
import { ColourStyleEnum, FontStyleEnum } from '../models/api';
import HowThisWorks from './HowThisWorks';
import AboutEvent from './AboutEvent';
import EndEvent from './EndEvent';
import PreviewBanner from '../components/PreviewBanner';
import FullMessage from './FullMessage';

export interface ShowingDetails {
  showingDetails: ActiveShowingDetails | null;
  sessionName: string;
  showingReference: string | null;
  previousPages: PageDetails[];
  allPages: PageDetails[] | null; // TODO: Nullable server-side?
  isActive: boolean;
  isEnded: boolean;
  isPresenting: boolean;
  showBlank: boolean;
  allowUserNavigation: boolean;
  allowViewAllPages: boolean;
  background: SessionBackgroundData | null;
  welcomeImageUrl: string | null;
  iconUrl: string | null;
  colourStyleEnum: ColourStyleEnum;
  fontStyleEnum: FontStyleEnum;
  aboutPage: PageDetails | null;
  endPage: EndShowingDetails | null;
}
export interface SessionBackgroundData {
  isVideo: boolean;
  url: string;
}

export interface LocalStateStore {
  showingReference: string;
}
export interface LocalStateStore {
  showingReference: string;
}
export interface ActiveShowingDetails {
  startDateUtc: string;
  endDateUtc: string | null;
  reference: string;
  location: string | null;
  isPresenting: boolean;
  isDefault: boolean;
}
export interface PageDetails {
  url: string | null;
  embed: boolean;
  name: string;
  reference: string;
}

export interface BrowserDetails {
  browserName: string;
  osName: string;
  browserVersion: string;
  osVersion: string;
  deviceType: string;
  fullBrowserVersion: string;
}
export enum ConnectionStatusEnum {
  Disconnected = 'Disconnected',
  Connected = 'Connected',
  Connecting = 'Connecting',
  Reconnecting = 'Reconnecting'
}
export enum ContentStatusEnum {
  Visible = 'Visible',
  Hiding = 'Hiding',
  Showing = 'Showing',
  Hidden = 'Hidden'
}
export interface EndShowingDetails {
  donationPageLink: string | null;
  feedbackLink: string | null;
  subscribeLink: string | null;
  pages: PageDetails[];
}
type SessionProps = {
  isPreview?: boolean | null;
};

const Session: React.FC<SessionProps> = ({ isPreview }) => {
  const [sessionName, setSessionName] = useState('');
  const [welcomeImageUrl, setWelcomeImageUrl] = useState<string | null>(null);
  const [iconUrl, setIconUrl] = useState<string | null>(null);
  const [colourStyleEnum, setColourStyleEnum] =
    useState<ColourStyleEnum | null>(null);
  const [fontStyleEnum, setFontStyleEnum] = useState<FontStyleEnum | null>(
    null
  );
  const [background, setBackground] = useState<SessionBackgroundData | null>(
    null
  );
  const [showingDetails, setShowingDetails] =
    useState<ActiveShowingDetails | null>();
  const [allowUserNavigation, setAllowUserNavigation] = useState(false);
  const [allowViewAllPages, setAllowViewAllPages] = useState(false);
  const [startAtLatest, setStartAtLatest] = useState(false);
  const [allowReview, setAllowReview] = useState(false);
  const [confirmView, setConfirmView] = useState<ShowingDetails | null>();
  const [showIntro, setShowIntro] = useState(true);
  const [showHowThisWorks, setShowHowThisWorks] = useState(false);
  const [showAbout, setShowAbout] = useState(false);
  const [aboutPage, setAboutPage] = useState<PageDetails | null>();
  const [endPage, setEndPage] = useState<EndShowingDetails | null>();
  const [isEnded, setIsEnded] = useState(false);
  const [isFull, setIsFull] = useState(false);
  const [isPresenting, setIsPresenting] = useState(false);
  const [isReviewingAfterEnded, setIsReviewingAfterEnded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pages, setPages] = useState([] as PageDetails[]);
  const [allPages, setAllPages] = useState([] as PageDetails[]);
  const [blank, setBlank] = useState(false);
  const [connection, setConnection] = useState<signalR.HubConnection | null>(
    null
  );
  const [connectionStatus, setConnectionStatus] = useState(
    ConnectionStatusEnum.Connecting
  );
  const [contentStatus, setContentStatus] = useState(ContentStatusEnum.Visible);

  const { reference } = useParams();
  const [searchParams] = useSearchParams();
  const autoJoin = searchParams.get('autoJoin');

  let viewingHistoric = false;

  const requestSessionData = (connection: signalR.HubConnection) => {
    connection
      .invoke('RequestSessionData', {
        reference,
        browserDetails: getBrowserDetails()
      })
      .then(() => console.log('Message sent successfully'))
      .catch((err) => console.error('Error sending message: ', err));
  };
  const requestHistoricData = (
    connection: signalR.HubConnection | null,
    historicShowingReference: string
  ) => {
    if (!connection) {
      console.log('Connection not present');
      return;
    }
    connection
      .invoke('RequestHistoricData', historicShowingReference)
      .then(() => console.log('Message sent successfully'))
      .catch((err) => console.error('Error sending message: ', err));
  };

  const updatePages = (newPages: PageDetails[]) => {
    setPages((previousPages) => [...previousPages, ...newPages]);
  };

  const updatePage = (newPage: PageDetails) => {
    setPages((previousPages) => {
      const lastPage = previousPages[previousPages.length - 1];
      const isSame = lastPage?.reference === newPage?.reference;

      return isSame ? previousPages : [...previousPages, newPage];
    });
  };

  const setViewingHistoric = (v: boolean) => {
    console.log(v);
    viewingHistoric = v;
  };

  const viewLive = (message: ShowingDetails) => {
    setStartAtLatest(true);
    setViewingHistoric(false);
    //setShowingReference(message.showingReference);
    setPages(message.previousPages);
    if (message.allowViewAllPages && message.allPages) {
      setAllPages(message.allPages);
    }
    localStorage.setItem(
      `pages:${reference}`,
      JSON.stringify({
        showingReference: message.showingReference
      } as LocalStateStore)
    );
  };

  const connectionReceivePage = (message: PageDetails) => {
    if (viewingHistoric) {
      return;
    }
    if (message) {
      if (blank) {
        setContentStatus(ContentStatusEnum.Hidden);
      } else {
        setContentStatus(ContentStatusEnum.Hiding);
      }
      setTimeout(() => {
        updatePage(message);
        setContentStatus(ContentStatusEnum.Showing);
      }, 500);
      setBlank(false);
    } else {
      setBlank(true);
    }
  };

  useEffect(() => {
    if (connection) {
      console.log('Connection already created');
      return;
    }
    console.log('Starting connection');
    const connectionX = new signalR.HubConnectionBuilder()
      .withUrl(`${process.env.REACT_APP_API_URL}/notesHub`)
      .withAutomaticReconnect()
      .build();

    connectionX.on('ReceiveSession', (message: ShowingDetails) => {
      if (!loading) {
        return;
      }
      setIsFull(false);
      setSessionName(message.sessionName);
      setWelcomeImageUrl(message.welcomeImageUrl);
      setIconUrl(message.iconUrl);
      setColourStyleEnum(message.colourStyleEnum);
      setFontStyleEnum(message.fontStyleEnum);
      setBackground(message.background);
      setShowingDetails(message.showingDetails);
      setAllowUserNavigation(message.allowUserNavigation);
      setAllowViewAllPages(message.allowViewAllPages);
      setAboutPage(message.aboutPage);
      setEndPage(message.endPage);
      setIsEnded(message.isEnded);
      setIsPresenting(message.isPresenting);
      if (message.showBlank) {
        setBlank(message.showBlank);
      }
      if (message.allPages) {
        if (message.isActive) {
          const previousData = localStorage.getItem(`pages:${reference}`);
          if (previousData) {
            const previousDataCast = JSON.parse(
              previousData
            ) as LocalStateStore;
            if (previousDataCast.showingReference != message.showingReference) {
              setConfirmView(message);
              setLoading(false);
              return;
            }
          }
          viewLive(message);
        } else {
          const previousData = localStorage.getItem(`pages:${reference}`);
          if (previousData) {
            setViewingHistoric(true);
            setAllowReview(true);
            const previousDataCast = JSON.parse(
              previousData
            ) as LocalStateStore;
            requestHistoricData(connectionX, previousDataCast.showingReference);
          }
        }
      }
      if (autoJoin) {
        setShowIntro(false);
      }
      setLoading(false);
    });

    connectionX.on('ShowingFull', () => {
      setIsFull(true);
      setLoading(false);
    });

    connectionX.on('ReceiveHistoricSession', (message: ShowingDetails) => {
      if (!loading) {
        return;
      }
      setSessionName(message.sessionName);
      setWelcomeImageUrl(message.welcomeImageUrl);
      setIconUrl(message.iconUrl);
      setShowingDetails(message.showingDetails);
      setViewingHistoric(true);
      if (message.isActive) {
        //setShowingReference(message.showingReference);
        if (message.allowViewAllPages && message.allPages) {
          updatePages(message.allPages);
          setAllPages(message.allPages);
        } else {
          updatePages(message.previousPages);
        }
        localStorage.setItem(
          `pages:${reference}`,
          JSON.stringify({
            showingReference: message.showingReference
          } as LocalStateStore)
        );
      } else {
        const previousData = localStorage.getItem(`pages:${reference}`);
        if (previousData) {
          JSON.parse(previousData) as LocalStateStore;
          //const previousDataCast = JSON.parse(previousData) as LocalStateStore;
          //setShowingReference(previousDataCast.showingReference);
        }
      }
      setLoading(false);
    });

    connectionX.on('ReceiveNewPage', (message: PageDetails) =>
      connectionReceivePage(message)
    );

    connectionX.on('EndShowing', (message: EndShowingDetails) => {
      setBlank(false);
      setEndPage(message);
      setIsEnded(true);
    });

    connectionX.on('ReOpenShowing', () => {
      setBlank(true);
      setEndPage(undefined);
      setIsEnded(false);
    });

    connectionX.on('StartShowing', () => {
      setIsPresenting(true);
    });

    // Function to send heartbeat to the server
    const sendHeartbeat = () => {
      connectionX
        .invoke('SendHeartbeat')
        .then(() => console.log('Heartbeat sent'))
        .catch((err) => console.error('Error sending heartbeat', err));
    };

    connectionX
      .start()
      .then(() => {
        setConnectionStatus(ConnectionStatusEnum.Connected);
        requestSessionData(connectionX);
        console.log('Connection established');

        const heartbeatInterval = setInterval(() => {
          sendHeartbeat();
        }, 30000); // 30 seconds

        return () => {
          clearInterval(heartbeatInterval); // Cleanup interval on unmount
        };
      })
      .catch((err) => console.error('Connection failed: ', err));

    connectionX.onreconnected(() => {
      setConnectionStatus(ConnectionStatusEnum.Connected);
      console.log('Reconnected to the server');
      requestSessionData(connectionX);
    });

    connectionX.onclose(() => {
      setConnectionStatus(ConnectionStatusEnum.Disconnected);
      console.log('Connection closed');
    });

    connectionX.onreconnecting(() => {
      setConnectionStatus(ConnectionStatusEnum.Reconnecting);
      console.log('Reconnecting to the server...');
    });

    setConnection(connectionX);
    setConnectionStatus(ConnectionStatusEnum.Connecting);

    return () => {
      connectionX.off('ReceiveSession');
      connectionX.off('ReceiveHistoricSession');
      connectionX.off('ReceiveNewPage');
      connectionX.stop();
    };
  }, []);

  useEffect(() => {
    const rootElement = document.getElementById('root');
    if (rootElement) {
      rootElement.scrollTo({ top: 0 });
    }
  }, [showHowThisWorks, showAbout, showIntro, startAtLatest]);

  if (loading) {
    return (
      <>
        <LoadingSpinner />
      </>
    );
  }

  const getStyleClass = () => {
    const g = colourStyleEnum
      ? `color-style-${ColourStyleEnum[colourStyleEnum]}`
      : '';
    const t = fontStyleEnum
      ? ` font-style-${FontStyleEnum[fontStyleEnum]}`
      : '';

    return 'content-page ' + g + t;
  };

  if (isFull) {
    return (
      <div className={getStyleClass()}>
        <Background background={background} />
        <PreviewBanner isPreview={isPreview} />
        <FullMessage />
      </div>
    );
  }

  if (showHowThisWorks) {
    return (
      <div className={getStyleClass()}>
        <Background background={background} />
        <PreviewBanner isPreview={isPreview} />
        <HowThisWorks back={() => setShowHowThisWorks(false)} />
      </div>
    );
  }

  if (showAbout && aboutPage) {
    return (
      <div className={getStyleClass()}>
        <Background background={background} />
        <PreviewBanner isPreview={isPreview} />
        <AboutEvent back={() => setShowAbout(false)} page={aboutPage} />
      </div>
    );
  }

  if (isEnded && endPage) {
    return (
      <div className={getStyleClass()}>
        <Background background={background} />
        <PreviewBanner isPreview={isPreview} />
        <EndEvent
          details={endPage}
          back={() => {
            setStartAtLatest(false);
            setIsReviewingAfterEnded(true);
            setIsEnded(false);
            setShowIntro(false);
          }}
        />
      </div>
    );
  }

  if (showIntro) {
    return (
      <div className={getStyleClass()}>
        <Background background={background} />
        <PreviewBanner isPreview={isPreview} />
        <Introduction
          sessionName={sessionName}
          welcomeImageUrl={welcomeImageUrl}
          iconUrl={iconUrl}
          location={showingDetails?.location}
          startDateUtc={showingDetails?.startDateUtc}
          isPresenting={isPresenting}
          allowReview={allowReview}
          chooseResume={!!confirmView}
          startAction={() => {
            if (confirmView) {
              viewLive(confirmView);
            }
            setShowIntro(false);
          }}
          setShowHowThisWorks={() => setShowHowThisWorks(true)}
          setShowAbout={() => setShowAbout(true)}
          showAboutAvailable={!!aboutPage}
          viewHistoricAction={() => {
            const previousData = localStorage.getItem(`pages:${reference}`);
            if (previousData) {
              setViewingHistoric(true);
              const previousDataCast = JSON.parse(
                previousData
              ) as LocalStateStore;
              requestHistoricData(
                connection,
                previousDataCast.showingReference
              );
            } else {
              if (confirmView) {
                viewLive(confirmView);
              }
            }

            setShowIntro(false);
          }}
          isDefault={showingDetails?.isDefault ?? false}
        />
      </div>
    );
  }

  return (
    <div className={getStyleClass()}>
      <Viewer
        pages={pages}
        allPages={allPages}
        blank={blank}
        allowUserNavigation={allowUserNavigation}
        allowViewAllPages={allowViewAllPages}
        connectionStatus={connectionStatus}
        startAtLatest={startAtLatest}
        contentStatus={contentStatus}
        isPreview={isPreview ?? false}
        background={background}
        isReviewingAfterEnded={isReviewingAfterEnded}
        viewEnd={() => setIsEnded(true)}
      />
      {connectionStatus === ConnectionStatusEnum.Disconnected && (
        <>
          <div className="connection-failed-background"></div>
          <div className="connection-failed">
            <span>
              We&apos;ve lost connection to the app. Please refresh the page.
            </span>
            <button
              className="on-white"
              onClick={() => window.location.reload()}
            >
              Refresh
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default Session;
